<template>
    <div class="bg-white w-full h-full min-h-screen flex flex-col pb-24" v-cloak>
        <div class="header-banner">
            <div class="alert alert-success" v-if="false">
                {{ banner }}
            </div>
        </div>

        <div :class="`flex bg-red-100`" v-if="isGhosting">
            <div class="flex-1 text-sm font-semibold text-center py-3 px-5">
                You are currently ghosting this account.
                <a :href="`${consumerPortalBaseUrl}/stop-ghosting`" :class="`block text-red-600 whitespace-nowrap`">
                    Stop Ghosting
                </a>
            </div>
        </div>
        <div :class="`flex bg-${$root.theme}-100`" v-if="$root.phoneNumberForLocation('header')">
            <div class="flex-1 text-sm font-semibold text-center py-3 px-5">
                Call us with questions or to pay by phone:
                <a :href="`tel:${$root.phoneNumberForLocation('header')}`" :class="`text-${$root.theme}-600 whitespace-nowrap`">
                    {{ $root.phoneNumberForLocation('header') }}
                </a>
            </div>
        </div>
        <header class="flex items-center" :class="{ 'shadow-xl': ! isSpecialOffersPage }">
            <div class="flex-1 pl-4 py-2">
                <img v-if="logo" :src="logo" class="logo" style="max-width: 195px;">
                <span class="text-lg font-semibold" v-else>{{ consumerPortalName }}</span>
            </div>
            <div class="flex-1 text-right pr-4 py-2 font-semibold">
                <a class="cursor-pointer" @click.prevent="showingModal.contact = true">
                    Contact Us
                </a>
            </div>
        </header>
        <div class="flex-1 flex flex-col">
            <slot :consumer-portal-name="consumerPortalName"
                :consumer-portal-base-url="consumerPortalBaseUrl"
                :consumer-portal-number="consumerPortalNumber"
                :consumer-portal-email="consumerPortalEmail"
                :deals="deals" />
        </div>
        <div class="border-t border-gray-200">
            <div class="mx-auto md:w-2/3 lg:w-1/2 xl:w-1/3 2xl:w-1/4 p-5 text-sm">
                <p v-show="false" v-if="isHalsted && ! isLoginPage" class="pb-2 text-justify">This communication is from a debt collector. This is an attempt to collect a debt and any information obtained will be used for that purpose.</p>
                <div :class="`text-${$root.theme}-700 text-center space-x-2`">
                    <a v-if="hasTermsAndConditions" href="#" @click.prevent="showingModal.termsOfUse = true" class="underline whitespace-nowrap">Terms Of Use</a>
                    <a v-if="hasDisclosures" href="#" @click.prevent="showingModal.disclosures = true" class="underline whitespace-nowrap">Federal & State Disclosures</a>
                    <a v-if="hasPrivacyPolicy" href="#" @click.prevent="showingModal.privacyPolicy = true" class="underline whitespace-nowrap">Privacy Policy</a>
                </div>
            </div>
        </div>

        <div class="fixed inset-0 overflow-y-auto" :class="{ 'z-10': overlayIsShown, 'z-neg': ! overlayIsShown }">
            <div class="flex items-start justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <transition
                    enter-active-class="ease-out duration-300"
                    enter-class="opacity-0"
                    enter-to-class="opacity-100"
                    leave-active-class="ease-in duration-200"
                    leave-class="opacity-100"
                    leave-to-class="opacity-0">
                    <div v-show="! Object.keys(showingOverlay).every(k => !showingOverlay[k])" class="fixed inset-0 transition-opacity" aria-hidden="true">
                        <div :class="`absolute inset-0 bg-gradient-to-b from-${$root.theme}-900 via-${$root.theme}-700 to-transparent`">
                            <svg v-if="showingOverlay.pdoExtra" class="w-full sm:hidden" height="200" viewBox="0 0 422 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <mask class="w-full" id="mask0_245_152" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" height="200">
                                    <rect class="w-full" x="0" height="199.615" rx="16" fill="#FFF6EE"/>
                                </mask>
                                <g mask="url(#mask0_245_152)">
                                    <path d="M406.137 66.5989L411.159 60.1787L408.711 52.4093C408.432 51.5255 409.289 50.7029 410.162 51.0199L417.826 53.8011L424.471 49.0691C425.228 48.53 426.275 49.0903 426.242 50.0199L425.956 58.1605L432.509 63.0051C433.256 63.5577 433.046 64.7238 432.15 64.9806L424.309 67.2289L421.713 74.9541C421.417 75.832 420.24 75.9961 419.722 75.2268L415.16 68.478L407.004 68.4056C406.075 68.3979 405.558 67.3301 406.132 66.5961L406.137 66.5989Z" fill="#FFF0BA"/>
                                    <path d="M283.058 12.9037L288.08 6.4835L285.632 -1.28587C285.353 -2.16971 286.211 -2.99232 287.083 -2.67533L294.748 0.105893L301.393 -4.62609C302.15 -5.1652 303.197 -4.60493 303.164 -3.67533L302.877 4.46525L309.431 9.30986C310.177 9.86251 309.967 11.0286 309.072 11.2854L301.231 13.5376L298.635 21.2628C298.34 22.1407 297.163 22.3048 296.645 21.5355L292.086 14.7856L283.93 14.7133C283.002 14.7056 282.488 13.6367 283.058 12.9037Z" fill="#FFD0A5"/>
                                    <path d="M371.615 -5.57153L362.65 -1.13458L366.229 7.45864L357.591 12.22L359.993 20.8552L354.109 25.4716L356.451 28.443L364.317 22.2654L362.037 14.0852L371.024 9.13365L367.508 0.679248L375.633 -3.33932L371.615 -5.57153Z" fill="#5DD8BF"/>
                                    <path d="M33.9793 75.3351L29.8313 66.2512L21.1133 69.5499L16.632 60.7782L7.91154 62.9046L3.48239 56.8895L0.432057 59.1321L6.35279 67.1776L14.6182 65.1605L19.278 74.2838L27.8548 71.0397L31.611 79.2742L33.9793 75.3351Z" fill="#FFF0BA"/>
                                    <path d="M146.671 14.3098L140.579 14.1428L139.769 4.32854L130.896 2.99369L129.086 -4.24876L125.411 -3.33133L127.83 6.35866L136.242 7.62362L137.087 17.8323L146.25 18.0805L146.671 14.3098Z" fill="#FFF0E1"/>
                                    <path d="M421.011 120.083L418.037 125.391L408.954 121.561L403.644 128.794L396.372 127.034L395.482 130.707L405.2 133.06L410.233 126.208L419.681 130.193L424.161 122.203L421.011 120.083Z" fill="white"/>
                                    <path d="M24.3099 114.936C28.854 120.573 27.9565 128.827 22.3101 133.368C16.6606 137.911 8.39677 137.024 3.85257 131.387C-0.691616 125.749 0.20283 117.497 5.8554 112.954C11.5049 108.411 19.7695 109.301 24.3129 114.935L24.3099 114.936Z" fill="#4EBEA7"/>
                                    <path d="M12.1306 128.872L15.3806 116.743" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                                    <path d="M18.8024 124.166L18.8139 124.159" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
                                    <path d="M8.69476 121.457L8.70627 121.451" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
                                    <path d="M286.69 101.078L289.769 108.09C290.103 108.85 291.165 108.898 291.563 108.169L295.266 101.459L302.894 100.697C303.723 100.614 304.097 99.622 303.527 99.0181L298.283 93.4357L299.921 85.9545C300.099 85.1416 299.27 84.4811 298.518 84.8339L291.574 88.0903L284.955 84.23C284.237 83.8128 283.353 84.3945 283.456 85.2203L284.41 92.8184L278.679 97.9149C278.061 98.4674 278.339 99.4901 279.156 99.6455L286.687 101.083L286.69 101.078Z" fill="#5DD8BF"/>
                                    <path d="M62.8818 28.0241L68.7311 29.9808C68.7311 29.9808 78.0362 27.0164 78.9601 25.1293C79.884 23.2421 80.1685 18.6581 81.4878 18.5401C82.8114 18.421 85.3625 18.8598 85.955 22.0033C86.3859 24.2849 83.0059 30.853 83.528 31.5583C83.8539 31.9975 87.1786 33.2174 89.6281 34.0713C91.236 34.6335 92.393 36.0508 92.6292 37.7611C92.7925 38.9228 92.1302 40.0425 91.0477 40.4572L89.3598 41.0989L89.8902 42.8365C90.2832 44.1307 89.5679 45.5186 88.284 45.9411L86.9796 46.3737L87.3926 47.4316C87.9201 48.7959 87.1585 50.3394 85.7528 50.7391L84.6136 51.0629L84.6587 51.3695C84.8453 52.6357 84.0459 53.8476 82.8165 54.1631L81.6821 54.4532L66.1058 49.2419C64.6476 48.755 63.3037 47.9697 62.1649 46.9309L56.9994 44.1767C56.444 43.8821 56.1913 43.2154 56.4047 42.6133L61.2821 28.8222C61.5193 28.1536 62.2343 27.8004 62.8936 28.0163L62.8818 28.0241Z" fill="#5DD8BF"/>
                                    <path d="M124.231 110.28C122.557 111.079 120.842 110.091 120.029 108.39C118.521 105.229 121.379 102.992 123.642 103.983C126.08 105.054 126.858 109.026 124.231 110.28ZM120.284 99.2784C120.502 99.8774 120.271 100.341 119.591 100.666C118.839 101.024 118.457 101.161 117.985 100.513C115.033 96.5081 113.266 93.8972 112.683 92.6775C107.203 81.002 106.416 80.0247 106.85 79.8177C107.5 79.5073 108.591 79.2562 109.267 78.9337C109.943 78.6112 110.844 77.9095 111.494 77.5991C111.845 77.4318 112.138 78.2774 113.337 80.7106C117.711 89.5775 118.772 94.439 120.285 99.2804L120.284 99.2784Z" fill="#5DD8BF"/>
                                    <path d="M371.224 83.3008C375.989 89.2121 375.048 97.8665 369.127 102.629C363.203 107.392 354.537 106.459 349.773 100.551C345.008 94.6396 345.946 85.9861 351.87 81.2231C357.794 76.4601 366.459 77.3894 371.224 83.3008Z" fill="#4EBEA7"/>
                                    <path d="M355.906 92.2298C355.134 93.5678 355.538 95.2394 356.813 95.976L359.417 97.4791C360.527 98.1198 361.974 97.6969 362.645 96.5352C363.375 95.2698 363.083 94.5064 362.431 93.7429L359.09 89.8775C358.438 89.114 358.146 88.3506 358.876 87.0852C359.547 85.9235 360.994 85.5006 362.104 86.1414L364.708 87.6445C365.983 88.381 366.388 90.0526 365.615 91.3906" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                    <path d="M364.346 85.5829L357.16 98.0296" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </g>
                                <circle cx="211.256" cy="82.3075" r="31.5" fill="#219F86"/>
                                <path d="M227.506 82.3075C227.506 91.2775 220.226 98.5575 211.256 98.5575C202.286 98.5575 195.006 91.2775 195.006 82.3075C195.006 73.3375 202.286 66.0575 211.256 66.0575C220.226 66.0575 227.506 73.3375 227.506 82.3075Z" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
                                <path d="M217.284 87.475L212.247 84.4687C211.369 83.9487 210.654 82.6975 210.654 81.6737V75.0112" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </div>
                    </div>
                </transition>

                <offer-overlay
                    :show="showingOverlay.pdoExtra"
                    :headline="`Check out this limited time offer!`"
                    :subtitle="`We added an extra payment to all of your plans!`"
                    @accepted="() => { this.deals.pdoExtraAccepted = true; this.showingOverlay.pdoExtra = false; }"
                    @declined="() => { this.deals.pdoExtraAccepted = false; this.showingOverlay.pdoExtra = false; }"
                    @close="showingOverlay.pdoExtra = false" />

            </div>
        </div>

        <div class="fixed inset-0 overflow-y-auto" :class="{ 'z-10': modalIsShown, 'z-neg': ! modalIsShown }">
            <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <transition
                    enter-active-class="ease-out duration-300"
                    enter-class="opacity-0"
                    enter-to-class="opacity-100"
                    leave-active-class="ease-in duration-200"
                    leave-class="opacity-100"
                    leave-to-class="opacity-0">
                    <div v-show="! Object.keys(showingModal).every(k => !showingModal[k])" class="fixed inset-0 transition-opacity" aria-hidden="true">
                        <div class="absolute inset-0 bg-gray-500" :class="{ 'opacity-75': ! heavyBlur, 'opacity-65': heavyBlur }"></div>
                    </div>
                </transition>
                <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <pdo-pop-up
                    :show="showingModal.pdo"
                    :headline="`Reduce the amount<br />of your first payment by<br />setting it up today!`"
                    :subtitle="`One time payments not<br />eligible for this offer`"
                    @accepted="() => { this.deals.pdoAccepted = true; this.showingModal.pdo = false; }"
                    @declined="() => { this.deals.pdoAccepted = false; this.showingModal.pdo = false; }"
                    @close="showingModal.pdo = false" />

                <special-offers-pop-up
                    :show="showingModal.specialOffer"
                    :headline="`Click for Better Offer!`"
                    accept-text="Better Offer"
                    @accepted="() => { this.showingModal.specialOffer = false; this.goToSpecialOffersUrl(); }"
                    @declined="() => { this.showingModal.specialOffer = false; }"
                    @close="showingModal.specialOffer = false" />

                <birthday-pop-up
                    :show="showingModal.birthday"
                    :headline="`Happy Birthday!<br />Special offers just<br />for you!`"
                    :accept-text="'Okay!'"
                    :decline-button="false"
                    icon="birthday"
                    @close="showingModal.birthday = false"
                    @accepted="showingModal.birthday = false" />

                <agreement-modal
                    :show="showingModal.agreement"
                    :agreement="agreement"
                    @agree="() => { this.after(); this.showingModal.agreement = false; }"
                    @close="showingModal.agreement = false" />

                <confirm-email-modal
                    :show="showingModal.confirmEmail"
                    :email="email"
                    @agree="() => { this.$root.$refs.slot.confirmEmail(true); this.showingModal.confirmEmail = false; }"
                    @close="() => { this.$root.$refs.slot.confirmEmail(false); this.showingModal.confirmEmail = false; }" />

                <confirm-offer-modal
                    :show="showingModal.confirmOffer"
                    @agree="() => { this.$root.$refs.slot.confirmOffer(true); this.showingModal.confirmOffer = false; }"
                    @close="() => { this.$root.$refs.slot.confirmOffer(false); this.showingModal.confirmOffer = false; }" />

                <prelit-legal-modal
                    :show="showingModal.prelitLegal"
                    :prelit="prelit"
                    @close="afterPrelitModal" />

                <mvn-modal
                    v-if="mvn"
                    :show="showingModal.mvn"
                    :referral-email="mvn.referral_email"
                    :referral-phone-number="mvn.referral_phone_number"
                    :template="mvn.template"
                    :can-email="!! mvn?.can_email"
                    :can-sms="!! mvn?.can_sms"
                    @close="afterMvnModal" />

                <credit-report-modal
                    :show="showingModal.creditReport"
                    :content="creditReportContent"
                    @close="showingModal.creditReport = false" />

                <contact-us-modal
                    :show="showingModal.contact"
                    :consumer-portal-email="consumerPortalEmail"
                    :consumer-portal-number="consumerPortalNumber"
                    @close="showingModal.contact = false" />

                <find-reference-modal
                    :show="showingModal.findReference"
                    @close="showingModal.findReference = false" />

                <terms-modal
                    :show="showingModal.termsOfUse"
                    @close="showingModal.termsOfUse = false" />

                <disclosures-modal
                    :show="showingModal.disclosures"
                    @close="showingModal.disclosures = false" />

                <billing-profile-expiration-warning-modal
                    :billing-profile="billingProfile"
                    :show="showingModal.billingProfileExpirationWarning"
                    @update="handleRequestToUpdateBillingProfile"
                    @close="showingModal.billingProfileExpirationWarning = false" />

                <privacy-policy-modal
                    :show="showingModal.privacyPolicy"
                    @close="showingModal.privacyPolicy = false" />

                <loading-modal
                    :show="showingModal.loading"
                    @close="showingModal.loading = false" />

                <find-c-v-v-modal
                    :show="showingModal.cvv"
                    @close="showingModal.cvv = false" />

                <account-details-modal
                    :show="showingModal.accountDetails"
                    :account-details="accountDetails"
                    :just-details="onlyShowAccountDetails"
                    @close="showingModal.accountDetails = false"
                    @after="afterAccountDetails" />

                <reschedule-declined-payment
                    :show="showingModal.paymentReschedule"
                    :payments="payments"
                    :pending-payments="pendingPayments"
                    :account="account"
                    :billing-profile="billingProfile"
                    :settings="settings"
                    @close="showingModal.paymentReschedule = false" />

                <make-extra-payment
                    :show="showingModal.paymentExtra"
                    :account="account"
                    :billing-profile="billingProfile"
                    :settings="settings"
                    @close="showingModal.paymentExtra = false" />

                <make-payment-early
                    :show="showingModal.paymentEarly"
                    :payment="payment"
                    :account="account"
                    :billing-profile="billingProfile"
                    :settings="settings"
                    @close="showingModal.paymentEarly = false" />

                <update-billing-profile
                    :show="showingModal.updateBillingProfile"
                    :payment="payment"
                    :account="account"
                    :billing-profile="billingProfile"
                    :settings="settings"
                    @close="showingModal.updateBillingProfile = false" />

                <update-contact-information
                    :show="showingModal.updateContactInformation"
                    :account="account"
                    @close="showingModal.updateContactInformation = false" />

                <add-payment-to-calendar-modal
                    :show="showingModal.addToCalendar"
                    :payment="payment"
                    :settings="settings"
                    @close="showingModal.addToCalendar = false" />

                <confirm-payment-information-modal
                    :show="showingModal.confirmPaymentInformation"
                    @agree="() => { this.$root.$refs.slot.confirmPaymentInformation(true); this.showingModal.confirmPaymentInformation = false; }"
                    @close="() => { this.$root.$refs.slot.confirmPaymentInformation(false); this.showingModal.confirmPaymentInformation = false; }" />

                <select-account-for-login-modal
                    :show="showingModal.selectAccountForLogin"
                    :accounts="accounts"
                    :with-close="false"
                    @select="account => { this.$root.$refs.slot.login(account); this.showingModal.selectAccountForLogin = false; this.heavyBlur = false; }" />

                <select-account-modal
                    :show="showingModal.selectAccount"
                    :accounts="accounts"
                    :with-close="true"
                    @select="account => { this.$root.$refs.slot.changeAccount(account); this.showingModal.selectAccount = false; this.heavyBlur = false; }"
                    @close="showingModal.selectAccount = false" />

                <electronic-consent-modal
                    :show="showingModal.electronicConsent"
                    :account-id="settings.account_id"
                    :template="settings.ecommunication_agreement.template"
                    :declined-template="settings.ecommunication_agreement.declined_template"
                    @accepted="showingModal.electronicConsent = false"
                    @declined="showingModal.electronicConsent = false" />

                <pdo-confirmation-modal
                    :show="showingModal.pdoConfirmation"
                    :pdo-config="pdoConfig"
                    @confirm="() => {
                        pdoAcceptedCallback();

                        showingModal.pdoConfirmation = false;
                    }"
                    @decline="() => {
                        pdoDeclinedCallback();

                        showingModal.pdoConfirmation = false;
                    }"
                />
            </div>
        </div>

        <div class="hidden" :class="tailwindTreeShakingClasses" />
    </div>
</template>

<script>
    import Mixins from './mixins';
    import TermsModal from './components/Modals/TermsModal';
    import LoadingModal from './components/Modals/LoadingModal';
    import AgreementModal from './components/Modals/AgreementModal';
    import ContactUsModal from './components/Modals/ContactUsModal';
    import DisclosuresModal from './components/Modals/DisclosuresModal';
    import MakePaymentEarly from './components/Modals/MakePaymentEarly';
    import PrelitLegalModal from './components/Modals/PrelitLegalModal';
    import ConfirmEmailModal from './components/Modals/ConfirmEmailModal';
    import ConfirmOfferModal from './components/Modals/ConfirmOfferModal';
    import CreditReportModal from "./components/Modals/CreditReportModal";
    import FindReferenceModal from './components/Modals/FindReferenceModal';
    import PrivacyPolicyModal from './components/Modals/PrivacyPolicyModal';
    import AccountDetailsModal from './components/Modals/AccountDetailsModal';
    import RetryDeclinedPayment from './components/Modals/RetryDeclinedPayment';
    import RescheduleDeclinedPayment from './components/Modals/ReschedulePayment.vue';
    import SelectAccountForLoginModal from './components/Modals/SelectAccountModal';
    import SelectAccountModal from './components/Modals/SelectAccountModal';
    import ConfirmPaymentInformationModal from './components/Modals/ConfirmPaymentInformationModal';
    import PdoPopUp from "./components/Modals/OfferPopUp.vue";
    import SpecialOffersPopUp from "./components/Modals/OfferPopUp.vue";
    import BirthdayPopUp from "./components/Modals/OfferPopUp.vue";
    import OfferOverlay from "./components/Modals/OfferOverlay.vue";
    import MvnModal from "./components/Modals/MvnModal.vue";
    import UpdateBillingProfile from "./components/Modals/UpdateBillingProfile.vue";
    import BillingProfileExpirationWarningModal from './components/Modals/BillingProfileExpirationWarningModal.vue';
    import UpdateContactInformation from "./components/Modals/UpdateContactInformation.vue";
    import AddPaymentToCalendarModal from "./components/Modals/AddPaymentToCalendar.vue";
    import FindCVVModal from "./components/Modals/FindCVVModal.vue";
    import ElectronicConsentModal from "./components/Modals/ElectronicConsentModal.vue";
    import MakeExtraPayment from './components/Modals/MakeExtraPayment.vue'
    import PdoConfirmationModal from './components/Modals/PdoConfirmationModal.vue'

    export default {
        mixins: [Mixins],

        components: {
            PdoConfirmationModal,
            MakeExtraPayment,
            FindCVVModal,
            ElectronicConsentModal,
            UpdateBillingProfile,
            UpdateContactInformation,
            AddPaymentToCalendarModal,
            MvnModal,
            PdoPopUp,
            SpecialOffersPopUp,
            BirthdayPopUp,
            OfferOverlay,
            TermsModal,
            LoadingModal,
            AgreementModal,
            ContactUsModal,
            DisclosuresModal,
            MakePaymentEarly,
            PrelitLegalModal,
            CreditReportModal,
            ConfirmOfferModal,
            ConfirmEmailModal,
            FindReferenceModal,
            PrivacyPolicyModal,
            SelectAccountModal,
            AccountDetailsModal,
            RetryDeclinedPayment,
            RescheduleDeclinedPayment,
            SelectAccountForLoginModal,
            ConfirmPaymentInformationModal,
            BillingProfileExpirationWarningModal,
        },

        props: [
            'logo',
            'link',
            'banner',
            'data-settings',
            'account-id',
            'is-halsted',
            'is-dynamic',
            'show-terms-and-conditions',
            'authenticated',
            'is-login-page',
            'consumer-portal-name',
            'consumer-portal-email',
            'consumer-portal-number',
            'consumer-portal-base-url',
            'is-ghosting',
            'consumer-portal-page-name-seed',
        ],

        data() {
            return {
                settings: null,
                email: '',
                prelit: '',
                after: null,
                agreement: '',
                account: null,
                accounts: null,
                payment: null,
                payments: null,
                pendingPayments: null,
                timeout: null,
                heavyBlur: false,
                modalIsShown: false,
                overlayIsShown: false,
                accountDetails: null,
                billingProfile: null,
                creditReportContent: null,
                deals: {
                    pdoAccepted: null,
                    extraPaymentAccepted: null
                },
                accountIdForOffer: null,
                showingOverlay: {
                    pdoExtra: false,
                },
                onlyShowAccountDetails: false,
                mvn: null,
                pdoConfig: null,
                pdoAcceptedCallback: null,
                pdoDeclinedCallback: null,
                showingModal: {
                    pdo: false,
                    specialOffer: false,
                    birthday: false,
                    mvn: false,
                    updateBillingProfile: false,
                    billingProfileExpirationWarning: false,
                    updateContactInformation: false,
                    addToCalendar: null,
                    electronicConsent: false,
                    pdoConfirmation: false,

                    cvv: false,
                    contact: false,
                    loading: false,
                    agreement: false,
                    termsOfUse: false,
                    disclosures: false,
                    prelitLegal: false,
                    confirmEmail: false,
                    creditReport: false,
                    confirmOffer: false,
                    paymentEarly: false,
                    paymentRetry: false,
                    findReference: false,
                    privacyPolicy: false,
                    accountDetails: false,
                    selectAccount: false,
                    paymentReschedule: false,
                    paymentExtra: false,
                    selectAccountForLogin: false,
                    confirmPaymentInformation: false
                },
                tailwindTreeShakingClasses: [
                    // primary
                    'shadow-primary-200/25',
                    'via-primary-50', 'via-primary-100', 'via-primary-200', 'via-primary-300', 'via-primary-400', 'via-primary-500', 'via-primary-600', 'via-primary-700', 'via-primary-800', 'via-primary-900',
                    'from-primary-50', 'from-primary-100', 'from-primary-200', 'from-primary-300', 'from-primary-400', 'from-primary-500', 'from-primary-600', 'from-primary-700', 'from-primary-800', 'from-primary-900',
                    'to-primary-50', 'to-primary-100', 'to-primary-200', 'to-primary-300', 'to-primary-400', 'to-primary-500', 'to-primary-600', 'to-primary-700', 'to-primary-800', 'to-primary-900',
                    'text-primary-50', 'text-primary-100', 'text-primary-200', 'text-primary-300', 'text-primary-400', 'text-primary-500', 'text-primary-600', 'text-primary-700', 'text-primary-800', 'text-primary-900',
                    'bg-primary-50', 'bg-primary-100', 'bg-primary-200', 'bg-primary-300', 'bg-primary-400', 'bg-primary-500', 'bg-primary-600', 'bg-primary-700', 'bg-primary-800', 'bg-primary-900',
                    'hover:bg-primary-50', 'hover:bg-primary-100', 'hover:bg-primary-200', 'hover:bg-primary-300', 'hover:bg-primary-400', 'hover:bg-primary-500', 'hover:bg-primary-600', 'hover:bg-primary-700', 'hover:bg-primary-800', 'hover:bg-primary-900',
                    'focus:ring-primary-50', 'focus:ring-primary-100', 'focus:ring-primary-200', 'focus:ring-primary-300', 'focus:ring-primary-400', 'focus:ring-primary-500', 'focus:ring-primary-600', 'focus:ring-primary-700', 'focus:ring-primary-800', 'focus:ring-primary-900',
                    'border-primary-50', 'border-primary-100', 'border-primary-200', 'border-primary-300', 'border-primary-400', 'border-primary-500', 'border-primary-600', 'border-primary-700', 'border-primary-800', 'border-primary-900',
                    'focus:border-primary-50', 'focus:border-primary-100', 'focus:border-primary-200', 'focus:border-primary-300', 'focus:border-primary-400', 'focus:border-primary-500', 'focus:border-primary-600', 'focus:border-primary-700', 'focus:border-primary-800', 'focus:border-primary-900',

                    // orange
                    'via-orange-50', 'via-orange-100', 'via-orange-200', 'via-orange-300', 'via-orange-400', 'via-orange-500', 'via-orange-600', 'via-orange-700', 'via-orange-800', 'via-orange-900',
                    'from-orange-50', 'from-orange-100', 'from-orange-200', 'from-orange-300', 'from-orange-400', 'from-orange-500', 'from-orange-600', 'from-orange-700', 'from-orange-800', 'from-orange-900',
                    'to-orange-50', 'to-orange-100', 'to-orange-200', 'to-orange-300', 'to-orange-400', 'to-orange-500', 'to-orange-600', 'to-orange-700', 'to-orange-800', 'to-orange-900',
                    'text-orange-50', 'text-orange-100', 'text-orange-200', 'text-orange-300', 'text-orange-400', 'text-orange-500', 'text-orange-600', 'text-orange-700', 'text-orange-800', 'text-orange-900',
                    'bg-orange-50', 'bg-orange-100', 'bg-orange-200', 'bg-orange-300', 'bg-orange-400', 'bg-orange-500', 'bg-orange-600', 'bg-orange-700', 'bg-orange-800', 'bg-orange-900',
                    'hover:bg-orange-50', 'hover:bg-orange-100', 'hover:bg-orange-200', 'hover:bg-orange-300', 'hover:bg-orange-400', 'hover:bg-orange-500', 'hover:bg-orange-600', 'hover:bg-orange-700', 'hover:bg-orange-800', 'hover:bg-orange-900',
                    'focus:ring-orange-50', 'focus:ring-orange-100', 'focus:ring-orange-200', 'focus:ring-orange-300', 'focus:ring-orange-400', 'focus:ring-orange-500', 'focus:ring-orange-600', 'focus:ring-orange-700', 'focus:ring-orange-800', 'focus:ring-orange-900',
                    'border-orange-50', 'border-orange-100', 'border-orange-200', 'border-orange-300', 'border-orange-400', 'border-orange-500', 'border-orange-600', 'border-orange-700', 'border-orange-800', 'border-orange-900',
                    'focus:border-orange-50', 'focus:border-orange-100', 'focus:border-orange-200', 'focus:border-orange-300', 'focus:border-orange-400', 'focus:border-orange-500', 'focus:border-orange-600', 'focus:border-orange-700', 'focus:border-orange-800', 'focus:border-orange-900',

                    // cyan
                    'via-cyan-50', 'via-cyan-100', 'via-cyan-200', 'via-cyan-300', 'via-cyan-400', 'via-cyan-500', 'via-cyan-600', 'via-cyan-700', 'via-cyan-800', 'via-cyan-900',
                    'from-cyan-50', 'from-cyan-100', 'from-cyan-200', 'from-cyan-300', 'from-cyan-400', 'from-cyan-500', 'from-cyan-600', 'from-cyan-700', 'from-cyan-800', 'from-cyan-900',
                    'to-cyan-50', 'to-cyan-100', 'to-cyan-200', 'to-cyan-300', 'to-cyan-400', 'to-cyan-500', 'to-cyan-600', 'to-cyan-700', 'to-cyan-800', 'to-cyan-900',
                    'text-cyan-50', 'text-cyan-100', 'text-cyan-200', 'text-cyan-300', 'text-cyan-400', 'text-cyan-500', 'text-cyan-600', 'text-cyan-700', 'text-cyan-800', 'text-cyan-900',
                    'bg-cyan-50', 'bg-cyan-100', 'bg-cyan-200', 'bg-cyan-300', 'bg-cyan-400', 'bg-cyan-500', 'bg-cyan-600', 'bg-cyan-700', 'bg-cyan-800', 'bg-cyan-900',
                    'hover:bg-cyan-50', 'hover:bg-cyan-100', 'hover:bg-cyan-200', 'hover:bg-cyan-300', 'hover:bg-cyan-400', 'hover:bg-cyan-500', 'hover:bg-cyan-600', 'hover:bg-cyan-700', 'hover:bg-cyan-800', 'hover:bg-cyan-900',
                    'focus:ring-cyan-50', 'focus:ring-cyan-100', 'focus:ring-cyan-200', 'focus:ring-cyan-300', 'focus:ring-cyan-400', 'focus:ring-cyan-500', 'focus:ring-cyan-600', 'focus:ring-cyan-700', 'focus:ring-cyan-800', 'focus:ring-cyan-900',
                    'border-cyan-50', 'border-cyan-100', 'border-cyan-200', 'border-cyan-300', 'border-cyan-400', 'border-cyan-500', 'border-cyan-600', 'border-cyan-700', 'border-cyan-800', 'border-cyan-900',
                    'focus:border-cyan-50', 'focus:border-cyan-100', 'focus:border-cyan-200', 'focus:border-cyan-300', 'focus:border-cyan-400', 'focus:border-cyan-500', 'focus:border-cyan-600', 'focus:border-cyan-700', 'focus:border-cyan-800', 'focus:border-cyan-900',

                    // indigo
                    'via-indigo-50', 'via-indigo-100', 'via-indigo-200', 'via-indigo-300', 'via-indigo-400', 'via-indigo-500', 'via-indigo-600', 'via-indigo-700', 'via-indigo-800', 'via-indigo-900',
                    'from-indigo-50', 'from-indigo-100', 'from-indigo-200', 'from-indigo-300', 'from-indigo-400', 'from-indigo-500', 'from-indigo-600', 'from-indigo-700', 'from-indigo-800', 'from-indigo-900',
                    'to-indigo-50', 'to-indigo-100', 'to-indigo-200', 'to-indigo-300', 'to-indigo-400', 'to-indigo-500', 'to-indigo-600', 'to-indigo-700', 'to-indigo-800', 'to-indigo-900',
                    'text-indigo-50', 'text-indigo-100', 'text-indigo-200', 'text-indigo-300', 'text-indigo-400', 'text-indigo-500', 'text-indigo-600', 'text-indigo-700', 'text-indigo-800', 'text-indigo-900',
                    'bg-indigo-50', 'bg-indigo-100', 'bg-indigo-200', 'bg-indigo-300', 'bg-indigo-400', 'bg-indigo-500', 'bg-indigo-600', 'bg-indigo-700', 'bg-indigo-800', 'bg-indigo-900',
                    'hover:bg-indigo-50', 'hover:bg-indigo-100', 'hover:bg-indigo-200', 'hover:bg-indigo-300', 'hover:bg-indigo-400', 'hover:bg-indigo-500', 'hover:bg-indigo-600', 'hover:bg-indigo-700', 'hover:bg-indigo-800', 'hover:bg-indigo-900',
                    'focus:ring-indigo-50', 'focus:ring-indigo-100', 'focus:ring-indigo-200', 'focus:ring-indigo-300', 'focus:ring-indigo-400', 'focus:ring-indigo-500', 'focus:ring-indigo-600', 'focus:ring-indigo-700', 'focus:ring-indigo-800', 'focus:ring-indigo-900',
                    'border-indigo-50', 'border-indigo-100', 'border-indigo-200', 'border-indigo-300', 'border-indigo-400', 'border-indigo-500', 'border-indigo-600', 'border-indigo-700', 'border-indigo-800', 'border-indigo-900',
                    'focus:border-indigo-50', 'focus:border-indigo-100', 'focus:border-indigo-200', 'focus:border-indigo-300', 'focus:border-indigo-400', 'focus:border-indigo-500', 'focus:border-indigo-600', 'focus:border-indigo-700', 'focus:border-indigo-800', 'focus:border-indigo-900',

                    // fuchsia
                    'via-fuchsia-50', 'via-fuchsia-100', 'via-fuchsia-200', 'via-fuchsia-300', 'via-fuchsia-400', 'via-fuchsia-500', 'via-fuchsia-600', 'via-fuchsia-700', 'via-fuchsia-800', 'via-fuchsia-900',
                    'from-fuchsia-50', 'from-fuchsia-100', 'from-fuchsia-200', 'from-fuchsia-300', 'from-fuchsia-400', 'from-fuchsia-500', 'from-fuchsia-600', 'from-fuchsia-700', 'from-fuchsia-800', 'from-fuchsia-900',
                    'to-fuchsia-50', 'to-fuchsia-100', 'to-fuchsia-200', 'to-fuchsia-300', 'to-fuchsia-400', 'to-fuchsia-500', 'to-fuchsia-600', 'to-fuchsia-700', 'to-fuchsia-800', 'to-fuchsia-900',
                    'text-fuchsia-50', 'text-fuchsia-100', 'text-fuchsia-200', 'text-fuchsia-300', 'text-fuchsia-400', 'text-fuchsia-500', 'text-fuchsia-600', 'text-fuchsia-700', 'text-fuchsia-800', 'text-fuchsia-900',
                    'bg-fuchsia-50', 'bg-fuchsia-100', 'bg-fuchsia-200', 'bg-fuchsia-300', 'bg-fuchsia-400', 'bg-fuchsia-500', 'bg-fuchsia-600', 'bg-fuchsia-700', 'bg-fuchsia-800', 'bg-fuchsia-900',
                    'hover:bg-fuchsia-50', 'hover:bg-fuchsia-100', 'hover:bg-fuchsia-200', 'hover:bg-fuchsia-300', 'hover:bg-fuchsia-400', 'hover:bg-fuchsia-500', 'hover:bg-fuchsia-600', 'hover:bg-fuchsia-700', 'hover:bg-fuchsia-800', 'hover:bg-fuchsia-900',
                    'focus:ring-fuchsia-50', 'focus:ring-fuchsia-100', 'focus:ring-fuchsia-200', 'focus:ring-fuchsia-300', 'focus:ring-fuchsia-400', 'focus:ring-fuchsia-500', 'focus:ring-fuchsia-600', 'focus:ring-fuchsia-700', 'focus:ring-fuchsia-800', 'focus:ring-fuchsia-900',
                    'border-fuchsia-50', 'border-fuchsia-100', 'border-fuchsia-200', 'border-fuchsia-300', 'border-fuchsia-400', 'border-fuchsia-500', 'border-fuchsia-600', 'border-fuchsia-700', 'border-fuchsia-800', 'border-fuchsia-900',
                    'focus:border-fuchsia-50', 'focus:border-fuchsia-100', 'focus:border-fuchsia-200', 'focus:border-fuchsia-300', 'focus:border-fuchsia-400', 'focus:border-fuchsia-500', 'focus:border-fuchsia-600', 'focus:border-fuchsia-700', 'focus:border-fuchsia-800', 'focus:border-fuchsia-900',

                    // red
                    'via-red-50', 'via-red-100', 'via-red-200', 'via-red-300', 'via-red-400', 'via-red-500', 'via-red-600', 'via-red-700', 'via-red-800', 'via-red-900',
                    'from-red-50', 'from-red-100', 'from-red-200', 'from-red-300', 'from-red-400', 'from-red-500', 'from-red-600', 'from-red-700', 'from-red-800', 'from-red-900',
                    'to-red-50', 'to-red-100', 'to-red-200', 'to-red-300', 'to-red-400', 'to-red-500', 'to-red-600', 'to-red-700', 'to-red-800', 'to-red-900',
                    'text-red-50', 'text-red-100', 'text-red-200', 'text-red-300', 'text-red-400', 'text-red-500', 'text-red-600', 'text-red-700', 'text-red-800', 'text-red-900',
                    'bg-red-50', 'bg-red-100', 'bg-red-200', 'bg-red-300', 'bg-red-400', 'bg-red-500', 'bg-red-600', 'bg-red-700', 'bg-red-800', 'bg-red-900',
                    'hover:bg-red-50', 'hover:bg-red-100', 'hover:bg-red-200', 'hover:bg-red-300', 'hover:bg-red-400', 'hover:bg-red-500', 'hover:bg-red-600', 'hover:bg-red-700', 'hover:bg-red-800', 'hover:bg-red-900',
                    'focus:ring-red-50', 'focus:ring-red-100', 'focus:ring-red-200', 'focus:ring-red-300', 'focus:ring-red-400', 'focus:ring-red-500', 'focus:ring-red-600', 'focus:ring-red-700', 'focus:ring-red-800', 'focus:ring-red-900',
                    'border-red-50', 'border-red-100', 'border-red-200', 'border-red-300', 'border-red-400', 'border-red-500', 'border-red-600', 'border-red-700', 'border-red-800', 'border-red-900',
                    'focus:border-red-50', 'focus:border-red-100', 'focus:border-red-200', 'focus:border-red-300', 'focus:border-red-400', 'focus:border-red-500', 'focus:border-red-600', 'focus:border-red-700', 'focus:border-red-800', 'focus:border-red-900',
                ],
                consumerPortalPageName: null,
            }
        },

        created() {
            this.settings = this.dataSettings;
        },

        mounted() {
            if (this.authenticated) {
                setInterval(() => {
                    this.$http.post(`${this.consumerPortalBaseUrl}/api/tracking`, {
                        event: 'touch'
                    }).then(r => {

                    }, e => {

                    });
                }, 10000); // 10 seconds
            }

            this.consumerPortalPageName = this.consumerPortalPageNameSeed;

            this.$root.setDefaultConsumerPortalNumber(this.consumerPortalNumber);
        },

        watch: {
            /**
             * Watch showingModal to open and close the modal accounting for transition timing.
             *
             * @return {void}
             */
            showingModal: {
                deep: true,
                handler: function (showingModal) {
                    if (this.timeout !== null) {
                        clearTimeout(this.timeout);
                    }

                    let modalIsShown = !Object.keys(showingModal).every(k => !showingModal[k]);

                    if (modalIsShown) {
                        this.modalIsShown = true;
                    } else {
                        this.timeout = setTimeout(() => {
                            this.modalIsShown = false;

                            this.timeout = null;
                        }, 350);
                    }
                }
            },

            /**
             * Watch showingModal to open and close the modal accounting for transition timing.
             *
             * @return {void}
             */
            showingOverlay: {
                deep: true,
                handler: function (showingOverlay) {
                    if (this.overlayTimeout !== null) {
                        clearTimeout(this.overlayTimeout);
                    }

                    let overlayIsShown = !Object.keys(showingOverlay).every(k => !showingOverlay[k]);

                    if (overlayIsShown) {
                        this.overlayIsShown = true;
                    } else {
                        this.overlayTimeout = setTimeout(() => {
                            this.overlayIsShown = false;

                            this.overlayTimeout = null;
                        }, 350);
                    }
                }
            },

            'showingModal.pdo'(showingPdoModal, oldShowingPdoModal) {
                if (showingPdoModal && ! oldShowingPdoModal) {
                    this.$http.post(`${this.consumerPortalBaseUrl}/api/tracking`, {
                        event: 'settlement-plans',
                        value: 'PDO-Popup'
                    }).then(r => {

                    }, e => {

                    });
                }
            },

            'showingOverlay.pdoExtra'(showingPdoExtraOverlay, oldShowingPdoExtraOverlay) {
                if (showingPdoExtraOverlay && ! oldShowingPdoExtraOverlay) {
                    this.$http.post(`${this.consumerPortalBaseUrl}/api/tracking`, {
                        event: 'settlement-plans',
                        value: 'PDO-Popup-Extra'
                    }).then(() => {}, () => {});
                }
            }
        },

        computed: {
            hasTermsAndConditions() {
                return window.terms_and_conditions_enabled;
            },

            hasPrivacyPolicy() {
                return window.privacy_policy_enabled;
            },

            hasDisclosures() {
                return window.disclosures_enabled;
            },

            hasFindReferenceHelp() {
                return window.find_reference_help_enabled;
            },

            isSpecialOffersPage() {
                return window.location.href.includes('special-offers');
            }
        },

        methods: {
            updatePageName(pageName) {
                this.consumerPortalPageName = pageName;
            },

            showAddToCalendarModalForPayment(payment) {
                this.payment = payment;
                this.showingModal.addToCalendar = true;
            },

            handleRequestToUpdateBillingProfile() {
                this.showingModal.billingProfileExpirationWarning = false;

                setTimeout(() => this.showUpdateBillingProfile(this.billingProfile), 500);
            },

            showUpdateBillingProfile(billingProfile) {
                this.billingProfile = billingProfile;
                this.showingModal.updateBillingProfile = true;
            },

            showBillingProfileExpirationWarning(billingProfile) {
                this.billingProfile = billingProfile;
                this.showingModal.billingProfileExpirationWarning = true;
            },

            afterPrelitModal() {
                this.showingModal.prelitLegal = false;

                setTimeout(() => this.showAccountDetails(), 1000);
            },

            afterAccountDetails() {
                if (this.mvn) {
                    setTimeout(() => this.showMvnTemplate(), 1000);
                } else {
                    this.$root.$emit('set-offer-timeouts');
                }
            },

            afterMvnModal() {
                this.showingModal.mvn = false;

                this.$root.$emit('set-offer-timeouts');
            },

            setAccountDetails(accountDetails) {
                this.accountDetails = accountDetails;
            },

            setMvn(mvn) {
                this.mvn = mvn;
            },

            /**
             * @returns {void}
             */
            showMvnTemplate() {
                this.showingModal.mvn = true;

                this.$http.get(`${this.consumerPortalBaseUrl}/api/mvn/viewed/${this.$root.tiny}`);
            },

            /**
             * @returns {void}
             */
            goToSpecialOffersUrl() {
                window.location = `${this.consumerPortalBaseUrl}/?i=${this.accountIdForOffer}&r=optOutSpecialOffers`;
            },

            showPdoConfirmation(pdoConfig, acceptedCallback, declinedCallback) {
                this.pdoConfig = pdoConfig;
                this.pdoAcceptedCallback = acceptedCallback;
                this.pdoDeclinedCallback = declinedCallback;

                this.showingModal.pdoConfirmation = true;
            },

            /**
             * @returns {void}
             */
            showSpecialOfferPopUp(accountId) {
                this.accountIdForOffer = accountId;

                this.showingModal.specialOffer = true;
            },

            /**
             * @returns {void}
             */
            toggleLoading() {
                this.showingModal.loading = ! this.showingModal.loading;
            },

            /**
             * @returns {void}
             */
            showOfferConfirm() {
                this.showingModal.confirmOffer = true;
            },

            /**
             * @returns {void}
             */
            showAccountDetails(justDetails = false) {
                this.showingModal.accountDetails = true;
                this.onlyShowAccountDetails = justDetails;
            },

            /**
             * @param {Object} agreement
             * @param {Object} callback
             * @returns {void}
             */
            showAgreement({ agreement, callback }) {
                this.agreement = agreement;
                this.after = callback;
                this.showingModal.agreement = true;
            },

            /**
             * @param {String} email
             * @returns {void}
             */
            showEmailConfirm(email) {
                this.email = email;
                this.showingModal.confirmEmail = true;
            },

            /**
             * @param {Object} prelit
             * @returns {void}
             */
            showPrelitStatus(prelit) {
                this.prelit = prelit;
                this.showingModal.prelitLegal = true;
            },

            /**
             * @param {Object} content
             * @returns {void}
             */
            showCreditReport(content) {
                this.creditReportContent = content;
                this.showingModal.creditReport = true;
            },

            /**
             * @param payments
             * @param account
             * @param billingProfile
             * @returns {void}
             */
            showPaymentReschedule({ payments, pendingPayments, account, billingProfile }) {
                this.payments = payments;
                this.pendingPayments = pendingPayments;
                this.account = account;
                this.billingProfile = billingProfile;
                this.showingModal.paymentReschedule = true;
            },

            showPaymentExtra({ account, billingProfile }) {
                this.account = account;
                this.billingProfile = billingProfile;
                this.showingModal.paymentExtra = true;
            },

            /**
             * @param payment
             * @param account
             * @param billingProfile
             * @returns {void}
             */
            showEarlyPayment({ payment, account, billingProfile }) {
                this.account = account;
                this.payment = payment;
                this.billingProfile = billingProfile;
                this.showingModal.paymentEarly = true;
            },

            /**
             * @returns {void}
             */
            showPaymentConfirm() {
                this.showingModal.confirmPaymentInformation = true;
            },

            /**
             * @param accounts
             * @returns {void}
             */
            showAccountsSelectForLogin(accounts) {
                this.accounts = accounts;
                this.heavyBlur = true;
                this.showingModal.selectAccountForLogin = true;
            },

            /**
             * @param accounts
             * @returns {void}
             */
            showAccountsSelect(accounts) {
                this.accounts = accounts;
                this.heavyBlur = true;
                this.showingModal.selectAccount = true;
            }
        }
    }
</script>
